import * as React from "react";
import { useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { groupBy } from "lodash-es";

import {
  DrawerContent,
  DrawerHeader,
  Panel,
  Typography,
  VStack,
} from "@smartrent/ui";

import { HardwareOrder } from "@/types";
import { formatDateToBeHumanReadable } from "@/utils";
import { formatCents } from "@/lib/helpers";

import { AddressLine } from "./AddressLine";

interface HardwareOrderViewDrawerProps extends HardwareOrder {}

const formatMultilineAddress = (order: HardwareOrder): string => {
  const parts = [
    order.shipping_address_line1,
    order.shipping_address_line2,
    `${order.shipping_address_city}, ${order.shipping_address_state} ${order.shipping_address_zip}`,
  ].filter(Boolean);

  return parts.join("\n");
};

export const HardwareOrderViewDrawer: React.FC<
  React.PropsWithChildren<HardwareOrderViewDrawerProps>
> = (order) => {
  const groupedLineItems = useMemo(() => {
    return groupBy(order.line_items, "product_type");
  }, [order.line_items]);

  const displayName = order.order_subtotal
    ? `${order.ordered_by_user?.first_name || ""} ${
        order.ordered_by_user?.last_name || ""
      }`.trim()
    : order.contact_info_name;
  return (
    <>
      <DrawerHeader title={`Order #${order.order_number}`} />
      <DrawerContent>
        <VStack spacing={24}>
          <Panel>
            <VStack spacing={16} style={styles.content}>
              <Typography type="title2">Shipping Information</Typography>

              {order.contact_info_po_number && (
                <View>
                  <Typography type="caption">PO Number</Typography>
                  <Typography type="body">
                    {order.contact_info_po_number}
                  </Typography>
                </View>
              )}

              <View>
                <Typography type="caption">Ordered Date</Typography>
                <Typography type="body">
                  {formatDateToBeHumanReadable({ date: order.submitted_at })}
                </Typography>
              </View>

              <View>
                <Typography type="caption">Ordered By</Typography>
                <Typography type="body">{displayName}</Typography>
              </View>

              <View>
                <Typography type="caption">Subtotal</Typography>
                <Typography type="body">
                  {formatCents(order.order_subtotal || 0)}
                </Typography>
              </View>

              <View>
                <AddressLine
                  variation="regular.caption.medium"
                  isCustom={order.is_custom_shipping_address}
                >
                  {order.is_custom_shipping_address
                    ? "Custom Shipping Address"
                    : "Shipping Address"}
                </AddressLine>
                {formatMultilineAddress(order)
                  .split("\n")
                  .map((line, index) => (
                    <AddressLine
                      key={index}
                      isCustom={order.is_custom_shipping_address}
                    >
                      {line}
                    </AddressLine>
                  ))}
              </View>

              <View>
                <Typography type="caption">Shipping Contact</Typography>
                <Typography type="body">
                  {order.shipping_address_contact_name}
                </Typography>
                <Typography type="body">
                  {order.shipping_address_contact_phone}
                </Typography>
                <Typography type="body">
                  {order.shipping_address_contact_email}
                </Typography>
              </View>
            </VStack>
          </Panel>

          <Panel>
            <VStack spacing={16} style={styles.content}>
              <Typography type="title2">Line Items</Typography>

              {Object.entries(groupedLineItems).map(([type, items]) => (
                <View key={type}>
                  <Typography type="title3" style={styles.categoryTitle}>
                    {type}
                  </Typography>
                  <VStack spacing={0}>
                    {items.map((item, index) => (
                      <View key={`${item.product_sku}-${index}`}>
                        <View style={styles.lineItemRow}>
                          <View style={styles.lineItemDetails}>
                            <Typography type="body" weight="semibold">
                              {item.product_name}
                            </Typography>
                            <Typography type="caption">
                              Unit Price: {formatCents(item.product_price)}
                            </Typography>
                          </View>
                          <View style={styles.lineItemPricing}>
                            <Typography type="caption">
                              Qty: {item.product_quantity}
                            </Typography>
                            <Typography type="body" weight="semibold">
                              {formatCents(
                                item.product_price * item.product_quantity
                              )}
                            </Typography>
                          </View>
                        </View>
                        {index < items.length - 1 && (
                          <View style={styles.divider} />
                        )}
                      </View>
                    ))}
                  </VStack>
                </View>
              ))}
            </VStack>
          </Panel>
        </VStack>
      </DrawerContent>
    </>
  );
};

const styles = StyleSheet.create({
  content: {
    padding: 16,
  },
  categoryTitle: {
    marginBottom: 12,
  },
  lineItemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 12,
  },
  lineItemDetails: {
    flex: 1,
  },
  lineItemPricing: {
    alignItems: "flex-end",
  },
  divider: {
    height: 1,
    backgroundColor: "#e0e0e0",
  },
});
